import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogout } from "../lib/api";
import { toast } from "sonner";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function StoreProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(() => load("sv_cart_v2", []));
  const [wishlist, setWishlist] = useState(() => load("sv_wishlist_v2", []));
  const [recentlyViewed, setRecentlyViewed] = useState(() => load("sv_recent_v2", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem("sv_cart_v2", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("sv_wishlist_v2", JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem("sv_recent_v2", JSON.stringify(recentlyViewed)); }, [recentlyViewed]);

  const refreshUser = useCallback(async () => {
    try { setUser(await authMe()); localStorage.setItem("sv_auth", "1"); }
    catch { setUser(null); localStorage.removeItem("sv_auth"); }
    finally { setAuthLoading(false); }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=") || !localStorage.getItem("sv_auth")) { setAuthLoading(false); return; }
    refreshUser();
  }, [refreshUser]);

  const logout = async () => { await authLogout(); localStorage.removeItem("sv_auth"); setUser(null); toast("Signed out"); };

  const addToCart = (product, color, size, quantity = 1) => {
    setCart((prev) => {
      const key = `${product.id}|${color || ""}|${size || ""}`;
      const idx = prev.findIndex((c) => `${c.product_id}|${c.color || ""}|${c.size || ""}` === key);
      if (idx >= 0) {
        const copy = [...prev]; copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity }; return copy;
      }
      return [...prev, { product_id: product.id, name: product.name, price: product.price,
        image: product.images?.[0], slug: product.slug, color, size, quantity }];
    });
    toast.success("Added to bag", { description: product.name });
    setCartOpen(true);
  };
  const updateQty = (i, q) => setCart((p) => p.map((c, idx) => idx === i ? { ...c, quantity: Math.max(1, q) } : c));
  const removeFromCart = (i) => setCart((p) => p.filter((_, idx) => idx !== i));
  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((w) => w.id === product.id);
      if (exists) { toast("Removed from wishlist"); return prev.filter((w) => w.id !== product.id); }
      toast.success("Saved to wishlist", { description: product.name });
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.images?.[0], slug: product.slug }];
    });
  };
  const inWishlist = (id) => wishlist.some((w) => w.id === id);

  const addRecent = (product) => setRecentlyViewed((prev) => {
    const filtered = prev.filter((p) => p.id !== product.id);
    return [{ id: product.id, name: product.name, price: product.price, image: product.images?.[0], slug: product.slug }, ...filtered].slice(0, 8);
  });

  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);
  const cartSubtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0);

  return (
    <StoreContext.Provider value={{
      user, setUser, authLoading, refreshUser, logout,
      cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartSubtotal, cartOpen, setCartOpen,
      wishlist, toggleWishlist, inWishlist,
      recentlyViewed, addRecent,
    }}>
      {children}
    </StoreContext.Provider>
  );
}
